<template>
  <BaseTypeEditShell
    :base-type="resolvedBaseType"
    :title="object?.name || editLabels.fallbackTitle"
    :subtitle="object?.name ? editLabels.subtitle : null"
    :in-review="isNeedsReview"
  >
    <template v-if="object">
      <Form
        ref="formObject"
        class="base-type-edit__form"
        :initial-values="object"
        :validation-schema="saveSchema"
        @submit="saveObject"
        v-slot="{ errors, setFieldValue, setFieldError, values }"
      >
        <NeedsReviewGuard :in-review="isNeedsReview">
              <FreePackageHint v-if="hasFreePackageForObjectBaseType" class="mb-4 mb-lg-5" :route-query="$route.query" />

              <fieldset class="fieldset mb-4 mb-lg-5">
                <legend>Allgemein</legend>
                <ObjectCategoriesReadonly />
                <div class="form-floating mb-3">
                  <Field
                    name="name"
                    id="form-object-name"
                    class="form-control"
                    :class="{'is-invalid': errors.name}"
                    autocomplete="off"
                    :placeholder="editLabels.namePlaceholder"
                  />
                  <label class="form-label" for="form-object-name">Titel</label>
                  <ErrorMessage name="name" :class="errorMessageClasses" />
                </div>
                <PremiumPhoneToggle
                  input-id="form-object-show-tel-fax"
                  :locked="!hasPaidOrIndividualPackageForObjectBaseType"
                  :phone-number="organisationPhone"
                  :route-query="$route.query"
                />
              </fieldset>

              <HostLocationSelector
                ref="hostLocationSelector"
                :base-type="object.base_type || packageBaseType"
                :errors="errors"
                :values="values"
                :initial-values="object"
                :host-objects="hostObjects"
                :set-field-value="setFieldValue"
                :set-field-error="setFieldError"
                @location-change="handleHostLocationChange"
              />

              <fieldset class="fieldset mb-4 mb-lg-5">
              <legend>Texte</legend>

              <div class="mb-3">
                <label class="form-label" for="form-object-content">Beschreibung</label>
                <Field name="content" v-slot="{ field, handleChange }">
                  <TextEditor :is-invalid="!!errors.content" v-model="field.value" @editorChange="handleChange" />
                </Field>
                <ErrorMessage name="content" :class="errorMessageClasses" />
              </div>
              </fieldset>

              <fieldset class="fieldset fieldset--media mb-4 mb-lg-5">
                <legend>Bilder zuweisen</legend>
                <p class="base-type-edit__media-intro">
                  <Icon icon="fa-pro-solid:circle-info" />
                  Wählen Sie die Bilder für dieses Objekt aus. Das erste Bild wird als Titelbild verwendet.
                </p>
                <Field
                  v-slot="{ handleChange }"
                  name="asset_ids"
                  type="text"
                  :value="tmpAssets.map(asset => asset.id)"
                >
                  <AssetSelect
                    :validation-error="!!errors.asset_ids"
                    :lock-type-select="true"
                    :base-type="packageBaseType"
                    :assets="tmpAssets"
                    @assetSelect="onAssetSelectChange($event, handleChange)"
                  />
                </Field>
                <ErrorMessage name="asset_ids" class="mb-3" :class="errorMessageClasses" />
              </fieldset>

            <div v-if="isAccommodationsForm" class="position-relative">
              <div v-if="hasFreePackageForObjectBaseType" class="premium-banderole">
                <span class="premium-banderole__label">premium</span>
              </div>
              <fieldset class="fieldset mb-4 mb-lg-5">
                <legend>Externer Kalender</legend>
                <p class="text-muted small mb-3">
                  Der externe Kalender steht in Premium-Paketen zur Verfügung.
                </p>
                <div class="row">
                  <div class="col-12 col-lg-6 mb-3">
                    <div class="form-floating">
                      <Field
                        name="external_ical.url"
                        id="form-object-external-ical-url"
                        class="form-control"
                        :class="{'is-invalid': errors['external_ical.url']}"
                        type="text"
                        placeholder="https://..."
                      />
                      <label class="form-label" for="form-object-external-ical-url">iCal-URL</label>
                      <ErrorMessage name="external_ical.url" :class="errorMessageClasses" />
                    </div>
                  </div>
                  <div class="col-12 col-lg-6 mb-3">
                    <div class="form-floating">
                      <Field
                        id="form-object-external-ical-style"
                        name="external_ical.style"
                        as="select"
                        class="form-select"
                        :class="{'is-invalid': errors['external_ical.style']}"
                      >
                        <option value="">Stil auswählen</option>
                        <option
                          v-for="option in externalIcalStyleOptions"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </Field>
                      <label class="form-label" for="form-object-external-ical-style">Kalender-Stil</label>
                      <ErrorMessage name="external_ical.style" :class="errorMessageClasses" />
                    </div>
                  </div>
                </div>
                <PremiumFeatureHint
                  :visible="hasFreePackageForObjectBaseType"
                  :route-query="$route.query"
                  :hint-text="premiumExternalIcalHint"
                />
              </fieldset>
            </div>

              <fieldset class="fieldset mb-4 mb-lg-5">
              <legend>Preise</legend>
              <div class="row">
                <div class="col-12 col-lg-6 mb-3">
                  <div class="form-floating">
                    <Field
                      name="marketing_price"
                      id="price-indication-cent"
                      class="form-control"
                      step="0.01"
                      type="number"
                      :class="{'is-invalid': errors.marketing_price}"
                      autocomplete="off"
                      placeholder="Preis"
                    />
                    <label class="form-label" for="price-indication-cent">(ab) Preis in Euro</label>
                    <ErrorMessage name="marketing_price" :class="errorMessageClasses" />
                  </div>
                </div>

                <div class="col-12 col-lg-6 mb-3">
                  <Field
                    v-slot="{ handleChange }"
                    name="marketing_price_description"
                    type="text"
                  >
                    <Select
                      v-model="priceSelect"
                      :options="priceSelectOptions"
                      optionLabel="name"
                      editable
                      placeholder="Preisdetails"
                      :class="{'is-invalid': errors.marketing_price_description}"
                      @change="handleChange(handlePriceSelectChange($event))"
                    />
                    <ErrorMessage name="marketing_price_description" :class="errorMessageClasses" />
                  </Field>
                </div>
              </div>
              </fieldset>

              <Field v-if="object.id" name="contact_form" v-slot="{ value, handleChange }">
                <ContactFormConfiguration
                  :model-value="value"
                  :booking-mode="values.inquiry_booking_mode"
                  :booking-product-config-id="values.inquiry_booking_product_config_id"
                  :external-booking-url="values.external_booking_url"
                  :external-booking-system="values.external_booking_system"
                  @update:model-value="handleChange"
                  @update:booking-mode="setFieldValue('inquiry_booking_mode', $event)"
                  @update:booking-product-config-id="setFieldValue('inquiry_booking_product_config_id', $event)"
                  @update:external-booking-url="setFieldValue('external_booking_url', $event)"
                />
              </Field>

            <div class="position-relative">
              <div v-if="hasFreePackageForObjectBaseType" class="premium-banderole">
                <span class="premium-banderole__label">premium</span>
              </div>
            <fieldset class="fieldset mb-4 mb-lg-5">
              <legend>Externer Link</legend>
              <p class="text-muted small mb-3">
                Der externe Website-Link steht in Premium-Paketen zur Verfügung.
              </p>
              <div class="row">
                <div class="col-12 col-lg-6 mb-3">
                  <div class="form-floating">
                    <Field
                      name="external_website_url"
                      id="form-object-external-website-url"
                      class="form-control"
                      :class="{'is-invalid': errors.external_website_url}"
                      type="text"
                      placeholder="https://..."
                    />
                    <label class="form-label" for="form-object-external-website-url">Externe Website URL</label>
                    <ErrorMessage name="external_website_url" :class="errorMessageClasses" />
                  </div>
                </div>
              </div>
              <PremiumFeatureHint
                :visible="hasFreePackageForObjectBaseType"
                :route-query="$route.query"
              />
              </fieldset>
            </div>

          <fieldset v-if="shouldShowWithdrawalWaiver(values)" class="fieldset mb-4 mb-lg-5">
            <legend>Widerrufsrecht</legend>
            <Field
              v-slot="{ field }"
              name="withdrawal_waiver_confirm"
              type="checkbox"
              :value="true"
              :unchecked-value="false"
            >
              <div class="form-check">
                <input
                  v-bind="field"
                  id="form-object-withdrawal-waiver-confirm"
                  class="form-check-input"
                  :class="{'is-invalid': errors.withdrawal_waiver_confirm}"
                  type="checkbox"
                >
                <label class="form-check-label" for="form-object-withdrawal-waiver-confirm">
                  Ich bin einverstanden und verlange ausdrücklich, dass die MANET Marketing GmbH vor Ende der
                  Widerrufsfrist mit der Vermarktungstätigkeit beginnt.
                  <br>
                  Mir ist bekannt, dass ich bei vollständiger Vertragserfüllung durch die MANET Marketing GmbH mein
                  Widerrufsrecht verliere, selbst wenn die Widerrufsfrist von 14 Tagen noch nicht abgelaufen ist.
                </label>
                <ErrorMessage name="withdrawal_waiver_confirm" :class="errorMessageClasses" />
              </div>
            </Field>
          </fieldset>

          <BaseTypeFormToolbar
            :errors="errors"
            :object-name="values.name"
            :package-base-type="packageBaseType"
            :enabled="publicationState.enabled"
            :marketed="publicationState.setting_enable_in_manet_stadt_abl"
            :has-website="showPortalToggle"
            :busy="publicationBusy"
            :publication-errors="publicationErrors"
            :publication-message="publicationMessage"
            @publication-action="runPublicationAction($event, values)"
            :admin2-object-id="object?.id"
            :admin2-lang="currentLang"
            :preview-url="previewUrl"
            :show-premium-preview="hasFreePackageForObjectBaseType"
            :show-standard-preview-with-premium="hasFreePackageForObjectBaseType"
          >
            <template #controls>
              <ObjectNeedsReviewIcon
                v-if="isNeedsReview"
                class="base-type-edit__toolbar-review"
                :label="reviewLabel"
              />
            </template>
          </BaseTypeFormToolbar>
        </NeedsReviewGuard>
      </Form>
    </template>
  </BaseTypeEditShell>
</template>

<script>
import AssetSelect from "@/components/general/AssetSelect.vue";
import ObjectCategoriesReadonly from "@/components/objects/ObjectCategoriesReadonly.vue";
import ObjectNeedsReviewIcon from "@/components/objects/ObjectNeedsReviewIcon.vue";
import {OBJECT_REVIEW_LABEL} from "@/utils/objectReview";
import BaseTypeEditShell from "@/components/base_type/BaseTypeEditShell.vue";
import BaseTypeFormToolbar from "@/components/base_type/BaseTypeFormToolbar.vue";
import FreePackageHint from "@/components/base_type/FreePackageHint.vue";
import PremiumPhoneToggle from "@/components/base_type/PremiumPhoneToggle.vue";
import PremiumFeatureHint from "@/components/base_type/PremiumFeatureHint.vue";
import HostLocationSelector from "@/components/objects/HostLocationSelector.vue";
import ContactFormConfiguration from "@/components/objects/ContactFormConfiguration.vue";
import {
  hasFreePackageForBaseType,
  hasPaidOrIndividualPackageForBaseType,
  MAX_OBJECT_IMAGES,
  MAX_OBJECT_NAME_LENGTH,
  PREMIUM_EXTERNAL_ICAL_HINT,
} from '@/utils/objectFormPolicy';
import {useMediaStore} from '@/stores/media';
import NeedsReviewGuard from "@/components/base_type/NeedsReviewGuard.vue";
import TextEditor from "@/components/TextEditor.vue";
import {useLocalizedObjectStore} from "@/stores/localized_objects";
import {useObjectStore} from "@/stores/object";
import {useUserStore} from "@/stores/user";
import {ErrorMessage, Field, Form} from "vee-validate";
import objectPublication from '@/mixins/objectPublication';
import Select from "primevue/select";
import Yup from "@/yup";
import {getBaseTypeTheme, resolveBaseTypeKey} from "@/utils/baseTypeTheme";
import {buildObjectPreviewUrl} from "@/utils/objectPreview";
import axios from "axios";
import {
  buildPhotonSearchUrl,
  filterPhotonHouseResults,
  mapPhotonFeatureToAddress,
  shouldSearchPhoton,
} from "@/scripts/locationSearch";

const EXTERNAL_ICAL_STYLES = [
  {label: 'Modern', value: 'modern'},
  {label: 'Klassisch', value: 'classic'},
  {label: 'Minimalistisch', value: 'minimalistic'},
]

const EDIT_LABELS = {
  accommodations: {
    fallbackTitle: 'Unterkunft bearbeiten',
    subtitle: 'Unterkunft bearbeiten',
    namePlaceholder: 'Titel der Unterkunft',
    save: 'Unterkunft speichern',
  },
  discover: {
    fallbackTitle: 'Eintrag bearbeiten',
    subtitle: 'Entdecken-Eintrag bearbeiten',
    namePlaceholder: 'Titel des Eintrags',
    save: 'Eintrag speichern',
  },
}

export default {
  mixins: [objectPublication],
  components: {
    AssetSelect,
    ObjectCategoriesReadonly,
    ObjectNeedsReviewIcon,
    BaseTypeEditShell,
    BaseTypeFormToolbar,
    ErrorMessage,
    Field,
    FreePackageHint,
    HostLocationSelector,
    ContactFormConfiguration,
    PremiumPhoneToggle,
    PremiumFeatureHint,
    Form,
    NeedsReviewGuard,
    Select,
    TextEditor,
  },
  setup() {
    const store = useUserStore()
    const localizedObjectStore = useLocalizedObjectStore()
    const objectStore = useObjectStore()
    const mediaStore = useMediaStore()

    return {
      localizedObjectStore,
      objectStore,
      store,
      mediaStore,
    }
  },
  data() {
    return {
      schema: Yup.object({
        location_id: Yup.number()
          .nullable()
          .test(
            'location-or-address',
            'Bitte Standort auswählen oder Adresse auswählen.',
            function (value) {
              return Boolean(this.parent.configured_host_object_id || value || String(this.parent.search || '').trim())
            }
          ),
        configured_host_object_id: Yup.number()
          .transform((value, originalValue) => originalValue === '' ? null : value)
          .nullable(),
        host_object_setting: Yup.boolean().nullable(),
        name: Yup.string().max(MAX_OBJECT_NAME_LENGTH).required().label('Titel'),
        // Both customer-facing activation variants use `enabled` as their publication state.
        content: Yup.string().label('Beschreibung').when('enabled', {
          is: true,
          then: schema => schema.required(),
        }),
        external_website_url: Yup.string().httpUrl().nullable().label('Externe Website URL'),
        external_booking_url: Yup.string().httpUrl().nullable().label('Link zum Anfrageformular / Buchungssystem')
          .when('inquiry_booking_mode', {
            is: 'external_url',
            then: schema => schema.required(),
          }),
        inquiry_booking_product_config_id: Yup.number().nullable(),
        external_ical: Yup.object({
          url: Yup.string().httpUrl().nullable().label('iCal-URL'),
          style: Yup.string()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .nullable()
            .oneOf([...EXTERNAL_ICAL_STYLES.map((option) => option.value), null])
            .label('Kalender-Stil'),
        }).nullable(),
        marketing_price: Yup.string().nullable().label('(ab) Preis'),
        marketing_price_description: Yup.string().nullable().label('Preisdetails'),
        asset_ids: Yup.array()
          .max(MAX_OBJECT_IMAGES, `Es dürfen maximal ${MAX_OBJECT_IMAGES} Bilder ausgewählt werden.`)
          .when('enabled', {
            is: true,
            then: schema => schema
              .min(1, 'Es wird mindestens ein Bild benötigt.')
              .required('Es wird mindestens ein Bild benötigt.'),
          }),
        withdrawal_waiver_confirm: Yup.boolean().when('enabled', {
          is: true,
          then: (schema) => schema.test(
            'withdrawal-waiver-required',
            'Bitte bestätigen Sie den Beginn der Vermarktung vor Ende der Widerrufsfrist.',
            (value) => !this.hasPaidPackageForObjectBaseType || this.hasWithdrawalWaiver || value === true
          ),
        }),
      }),
      errorMessageClasses: 'mt-1 d-block invalid-feedback',
      object: null,
      priceSelect: null,
      externalIcalStyleOptions: EXTERNAL_ICAL_STYLES,
      premiumExternalIcalHint: PREMIUM_EXTERNAL_ICAL_HINT,
      hostObjects: [],
      priceSelectOptions: [
        {name: 'pro Nacht', value: 'pro Nacht'},
        {name: 'Gesamtpreis', value: 'Gesamtpreis'},
        {name: 'pro Teilnehmer', value: 'pro Teilnehmer'},
        {name: 'pro Person', value: 'pro Person'},
        {name: 'Gesamtpreis (pro Person)', value: 'Gesamtpreis (pro Person)'},
      ],
      tmpAssets: [],
    }
  },
  computed: {
    publicationObject() {
      return this.object
    },
    publicationForm() {
      return this.$refs.formObject
    },
    reviewLabel() {
      return OBJECT_REVIEW_LABEL
    },
    resolvedBaseType() {
      return resolveBaseTypeKey(this.$route.name?.split('.')[0] || 'accommodations')
    },
    packageBaseType() {
      return getBaseTypeTheme(this.resolvedBaseType).apiValue
    },
    editLabels() {
      return EDIT_LABELS[this.resolvedBaseType] || EDIT_LABELS.accommodations
    },
    isAccommodationsForm() {
      return this.resolvedBaseType === 'accommodations'
    },
    locationId() {
      return this.object?.location_id ?? null
    },
    hasSubmittableLocation() {
      const loc = this.object?.location
      return Boolean(this.locationId || (loc?.street && String(loc?.name || '').trim()))
    },
    standortModel() {
      const loc = this.object?.location
      if (!loc || typeof loc !== 'object') {
        return null
      }
      const parts = [loc.street, [loc.zip, loc.town].filter(Boolean).join(' ')].filter(Boolean).join(', ')
      return {
        id: loc.id,
        name: loc.name,
        street: loc.street,
        zip: loc.zip,
        town: loc.town,
        country: loc.country,
        latitude: loc.latitude ?? loc.lat,
        longitude: loc.longitude ?? loc.lon,
        address_string: [loc.name, parts].filter(Boolean).join(' — '),
      }
    },
    currentLang() {
      return this.$route.params.lang || 'de'
    },
    isNeedsReview() {
      return !!this.object?.needs_review
    },
    showPortalToggle() {
      return false
    },
    hasFreePackageForObjectBaseType() {
      return hasFreePackageForBaseType(
        this.store.user?.organisation?.active_packages,
        this.packageBaseType,
      )
    },
    hasPaidOrIndividualPackageForObjectBaseType() {
      return hasPaidOrIndividualPackageForBaseType(
        this.store.user?.organisation?.active_packages,
        this.packageBaseType,
      )
    },
    organisationPhone() {
      return this.store.user?.organisation?.phone || ''
    },
    hasPaidPackageForObjectBaseType() {
      const activePackages = this.store.user?.organisation?.active_packages || []
      return activePackages.some((pkg) => (
        String(pkg?.base_type || '').toLowerCase() === this.packageBaseType
        && String(pkg?.source_kind || '').toLowerCase() === 'paid'
      ))
    },
    hasWithdrawalWaiver() {
      return !!this.object?.withdrawal_waiver_at
    },
    previewUrl() {
      return buildObjectPreviewUrl(this.object, this.currentLang)
    },
  },
  async mounted() {
    this.mediaStore.filters.type = ['images']
    const [{data}, hostResponse] = await Promise.all([
      this.localizedObjectStore.apiShow(this.$route.params.id, this.currentLang),
      this.objectStore.apiHostCandidates(this.currentLang, this.$route.params.id),
    ])
    this.hostObjects = Array.isArray(hostResponse.data?.host_objects)
      ? hostResponse.data.host_objects
      : []
    this.applyLoadedObjectData(data)
    this.store.fetchMyClients()
  },
  methods: {
    applyLoadedObjectData(data) {
      const normalizedObject = {
        ...data,
        base_type: data.base_type || this.packageBaseType,
        location_id: data.location_id ?? null,
        configured_host_object_id: data.configured_host_object_id ?? null,
        host_object_setting: data.host_object_setting ?? false,
        asset_ids: Array.isArray(data.assets) ? data.assets.map((asset) => asset.id) : [],
        content: data.content || '',
        enabled: !!data.enabled,
        show_tel_fax: !!data.show_tel_fax,
        withdrawal_waiver_at: data.withdrawal_waiver_at || null,
        withdrawal_waiver_confirm: data.withdrawal_waiver_confirm ?? !!data.withdrawal_waiver_at,
        // Euro like the input; a pending review value comes under the same key.
        marketing_price: data.marketing_price ?? null,
        marketing_price_description: data.marketing_price_description || null,
        setting_enable_in_manet_stadt_abl: !!data.enabled,
        external_ical: {
          url: data.external_ical?.url || '',
          style: data.external_ical?.style || '',
        },
      }

      this.object = normalizedObject
      this.tmpAssets = [...(data.assets || [])]
      this.priceSelect = normalizedObject.marketing_price_description

      if (this.$refs.formObject?.resetForm) {
        this.$refs.formObject.resetForm({
          values: normalizedObject,
        })
      }
    },
    handleHostLocationChange({id, location}) {
      this.object = {
        ...this.object,
        location_id: id,
        location,
      }
    },
    handleAddressChange(newAddress, setFieldValue) {
      if (!newAddress?.id && !newAddress?.street) {
        this.object = {
          ...this.object,
          location_id: null,
          location: null,
        }
        setFieldValue('location_id', null, true)
        return
      }

      const lid = newAddress.id ? Number(newAddress.id) : null
      this.object = {
        ...this.object,
        location_id: lid,
        location: this.normalizeLocationFromAddress(newAddress, lid),
      }
      setFieldValue('location_id', lid, true)

      if (lid) {
        this.store.fetchMyClients(true)
      }
    },
    normalizeLocationFromAddress(address, id = address?.id ?? null) {
      return {
        id,
        name: address.name,
        street: address.street,
        zip: address.zip ?? address.postcode,
        town: address.town ?? address.city,
        country: address.country,
        latitude: address.latitude ?? address.lat,
        longitude: address.longitude ?? address.lon,
      }
    },
    createAddressPayloadFromLocation(location) {
      return {
        name: location.name,
        street: location.street,
        postcode: location.zip,
        city: location.town,
        country: location.country,
        lat: location.latitude,
        lon: location.longitude,
      }
    },
    async resolveLocationFromSearch(values) {
      const query = String(values.search || '').trim()
      if (!shouldSearchPhoton(query)) {
        return null
      }

      const {data} = await axios.get(buildPhotonSearchUrl(query))
      const [feature] = filterPhotonHouseResults(data.features)
      if (!feature) {
        return null
      }

      const address = mapPhotonFeatureToAddress(feature)
      return this.normalizeLocationFromAddress({
        ...address,
        name: values.address_name || values.name || address.street,
      })
    },
    async ensureLocationForSubmit(values) {
      const submittedLocationName = String(values.address_name || '').trim()
      const existingLocationName = String(this.object?.location?.name || '').trim()
      const shouldKeepExistingLocation = values.location_id
        && (!submittedLocationName || submittedLocationName === existingLocationName)

      if (shouldKeepExistingLocation) {
        return Number(values.location_id)
      }

      const location = this.object?.location
        ? {
          ...this.object.location,
          name: submittedLocationName || this.object.location.name,
        }
        : await this.resolveLocationFromSearch(values)
      if (!location?.street || !String(location?.name || '').trim()) {
        return null
      }

      const created = await this.store.createLocationFromAddress(this.createAddressPayloadFromLocation(location))
      if (!created?.id) {
        return null
      }

      const lid = Number(created.id)
      this.object = {
        ...this.object,
        location_id: lid,
        location: this.normalizeLocationFromAddress(created, lid),
      }
      values.location_id = lid
      this.store.fetchMyClients(true)
      return lid
    },
    async persistObject(values) {
      const payload = {
        ...values,
        asset_ids: this.tmpAssets.map((asset) => asset.id),
        id: this.object.id,
        lang: this.currentLang,
      }
      if (this.hasPaidPackageForObjectBaseType && !this.hasWithdrawalWaiver && payload.enabled && payload.withdrawal_waiver_confirm) {
        payload.withdrawal_waiver_at = new Date().toISOString()
      }
      delete payload.withdrawal_waiver_confirm

      if (!this.showPortalToggle) {
        payload.setting_enable_in_manet_stadt_abl = !!payload.enabled
      }

      if (!this.isAccommodationsForm) {
        delete payload.external_ical
      }
      delete payload.description

      const {data} = await this.localizedObjectStore.apiUpdate(payload)
      this.applyLoadedObjectData({
        ...values,
        ...data,
        assets: data?.assets ?? this.tmpAssets,
        withdrawal_waiver_confirm: values.withdrawal_waiver_confirm,
      })
    },
    async persistPublicationState(state) {
      await this.localizedObjectStore.apiUpdate({id: this.object.id, lang: this.currentLang, ...state})
      this.object = {...this.object, ...state}
    },
    shouldShowWithdrawalWaiver() {
      return this.hasPaidPackageForObjectBaseType && !this.hasWithdrawalWaiver
    },
    handlePriceSelectChange(e) {
      if (typeof e.value?.value !== 'undefined') {
        return e.value.value
      }

      return e.value
    },
    onAssetSelectChange(selectedAssets, handleChange) {
      this.tmpAssets = selectedAssets
      handleChange(this.tmpAssets.map((asset) => asset.id))
    },
  },
}
</script>
